/**

* Handler hooks for member provider selection name steps (clinic and OBGYN).
 *
 * These hooks do NOT fetch data. The step components call useMemberProviderSelectionQuery
 * and useCheckRoutingInfoQueryCache, then pass the result into these hooks to get
 * submit/skip handlers.
 *
 * - Member Provider Selection: User's provider choice saved during routing
 * - Provider Finder Provider: Different data source (provider search feature)
 */

import { useCallback } from "react"
import { useUpdateRoutingInfo } from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useMemberProviderSelectionMutation } from "#/components/carrot-plans/hooks/useMemberProviderSelection"
import { MemberProviderSelection } from "#/types/memberProviderSelection"
import {
  FormValues,
  createFormSubmitRequest,
  createClinicSkipUpsertRequest,
  extractStringValue,
  createObGynSkipUpsertRequest,
  PROVIDER_CONSTANTS
} from "#/components/carrot-plans/utils/providerSelectionUtils"

// ─── useClinicNameStepHandlers ─────────────────────────────────────────────────

interface UseClinicNameStepHandlersParams {
  /** From routing cache (useCheckRoutingInfoQueryCache). */
  routingInfoId: number | undefined
  /** Saved member provider selection when the query has finished loading; null while loading. Pass from step component. */
  selectionWhenReady: MemberProviderSelection | null
  /** Whether the user previously skipped this step (from hasQuestionBeenSkipped). */
  wasSkipped: boolean
}

interface UseClinicNameStepHandlersReturn {
  createSubmitHandler: (send: (value: string) => void) => (values: FormValues) => Promise<void>
  createSkipHandler: (send: (value: string) => void) => () => Promise<void>
}

/**
 * Returns submit and skip handlers for the clinic name step.
 * Call from the step component after fetching routing and member provider selection; pass routingInfoId, selectionWhenReady, and wasSkipped.
 * Submit handler treats an empty form (no provider, no freeform name) as skip and calls the skip handler.
 */
export function useClinicNameStepHandlers({
  routingInfoId,
  selectionWhenReady,
  wasSkipped
}: UseClinicNameStepHandlersParams): UseClinicNameStepHandlersReturn {
  const { mutateAsync: upsertMemberProviderSelection } = useMemberProviderSelectionMutation()
  const { mutateAsync: updateRoutingInfoAsync } = useUpdateRoutingInfo()

  const previousAction = selectionWhenReady?.providerSelectionAction ?? null
  const previousProviderType = selectionWhenReady?.providerType ?? null
  const sawProviderTypeStep = selectionWhenReady?.sawProviderTypeStep ?? false

  const createSkipHandler = useCallback(
    (send: (value: string) => void) => async () => {
      if (routingInfoId != null) {
        await upsertMemberProviderSelection(createClinicSkipUpsertRequest(selectionWhenReady, routingInfoId))
      }
      await updateRoutingInfoAsync({ skipFields: ["nameOfClinic"] })
      send("")
    },
    [routingInfoId, selectionWhenReady, upsertMemberProviderSelection, updateRoutingInfoAsync]
  )

  const createSubmitHandler = useCallback(
    (send: (value: string) => void) => {
      return async (values: FormValues): Promise<void> => {
        const noProvider = values.noProvider === PROVIDER_CONSTANTS.NO_PROVIDER_RADIO_VALUE
        const hasProvider = !!values.provider?.uuid
        const hasFreeformName = !!extractStringValue(values.providerName)?.trim()

        if (!noProvider && !hasProvider && !hasFreeformName) {
          await createSkipHandler(send)()
          return
        }

        const request = createFormSubmitRequest(
          values,
          routingInfoId ?? null,
          previousAction,
          previousProviderType,
          sawProviderTypeStep
        )
        if (!request) return
        await upsertMemberProviderSelection(request)
        if (wasSkipped) {
          await updateRoutingInfoAsync({ clearProviderSkipFields: ["nameOfClinic"] })
        }
        send("")
      }
    },
    [
      routingInfoId,
      previousAction,
      previousProviderType,
      sawProviderTypeStep,
      wasSkipped,
      createSkipHandler,
      upsertMemberProviderSelection,
      updateRoutingInfoAsync
    ]
  )

  return { createSubmitHandler, createSkipHandler }
}

// ─── useObGynNameStepHandlers ──────────────────────────────────────────────────

interface UseObGynNameStepHandlersParams {
  /** From routing cache (useCheckRoutingInfoQueryCache). */
  routingInfoId: number | undefined
  /** Saved member provider selection when the query has finished loading; null while loading. Pass from step component. */
  selectionWhenReady: MemberProviderSelection | null
  /** Whether the user previously skipped this step (from hasQuestionBeenSkipped). */
  wasSkipped: boolean
}

interface UseObGynNameStepHandlersReturn {
  createSaveHandler: (
    send: (value: string) => void
  ) => (firstName: string | null, lastName: string | null) => Promise<void>
  createSkipHandler: (send: (value: string) => void) => () => Promise<void>
}

/**
 * Returns save and skip handlers for the OBGYN name step.
 * Call from the step component after fetching routing and member provider selection; pass routingInfoId, selectionWhenReady, and wasSkipped.
 */
export function useObGynNameStepHandlers({
  routingInfoId,
  selectionWhenReady,
  wasSkipped
}: UseObGynNameStepHandlersParams): UseObGynNameStepHandlersReturn {
  const { mutateAsync: upsertMemberProviderSelection } = useMemberProviderSelectionMutation()
  const { mutateAsync: updateRoutingInfoAsync } = useUpdateRoutingInfo()

  const existingAction = selectionWhenReady?.providerSelectionAction
  const hasPreviousProviderData = !!(
    selectionWhenReady &&
    (selectionWhenReady.providerFirstName || selectionWhenReady.providerLastName)
  )

  const createSkipHandler = useCallback(
    (send: (value: string) => void) => async () => {
      if (hasPreviousProviderData && routingInfoId != null) {
        await upsertMemberProviderSelection(createObGynSkipUpsertRequest(selectionWhenReady, routingInfoId))
      }
      await updateRoutingInfoAsync({ skipFields: ["nameOfObGyn"] })
      send("")
    },
    [hasPreviousProviderData, routingInfoId, selectionWhenReady, upsertMemberProviderSelection, updateRoutingInfoAsync]
  )

  const createSaveHandler = useCallback(
    (send: (value: string) => void) => {
      return async (firstName: string | null, lastName: string | null): Promise<void> => {
        if (!routingInfoId) return
        await upsertMemberProviderSelection({
          selectionSource: "ROUTING",
          providerType: PROVIDER_CONSTANTS.OBGYN_PROVIDER_TYPE,
          providerSelectionAction: existingAction,
          providerFirstName: firstName,
          providerLastName: lastName,
          routingInfoId
        })
        if (wasSkipped) {
          await updateRoutingInfoAsync({ clearProviderSkipFields: ["nameOfObGyn"] })
        }
        send("")
      }
    },
    [routingInfoId, existingAction, wasSkipped, upsertMemberProviderSelection, updateRoutingInfoAsync]
  )

  return { createSaveHandler, createSkipHandler }
}
